import { useEffect } from 'react'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import QuestionAnswerOutlined from '@mui/icons-material/QuestionAnswerOutlined'
import { Box, Dialog, Fab, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { AssistantPage } from '../pages/AssistantPage'
import { VigaIcon } from './VigaIcon'
import { VIGA } from './viga'
import { t } from '../i18n'

/**
 * s67 / ADR-0033 2i (V3, padrao Attio; Marcelo 25/10/2026 12:49 "V3 sim") -- a VIGA EM TODA TELA.
 *
 * Um botao redondo no canto de baixo, a direita, abre um PAINEL LATERAL (~400 px) com a MESMA conversa da pagina
 * /assistente: a conversa vive no cache do React Query (ASSISTANT_CONVERSATION_KEY), entao abrir, fechar, trocar de tela e
 * ir a /assistente mostram a mesma conversa. Comeca FECHADO. Em /assistente o botao e o painel NAO aparecem (a pagina ja
 * e a conversa). No celular (abaixo de 900 px) o painel abre em TELA CHEIA.
 * Com a IA ligada o botao e a VIGA ("Abrir a VIGA"); sem IA o icone e NEUTRO ("Abrir o assistente") -- D9: VIGA so com IA.
 * So aparece para quem pode usar o assistente (a permissao do item do menu).
 */
const DOCK_WIDTH = 400

export function VigaDock({
  open,
  iaLigada,
  onOpen,
  onClose,
}: {
  open: boolean
  iaLigada: boolean
  onOpen: () => void
  onClose: () => void
}) {
  const { pathname } = useLocation()
  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
  const naPagina = pathname === '/assistente'

  // ir para a pagina do assistente fecha o painel (a pagina e a mesma conversa)
  useEffect(() => {
    if (naPagina && open) onClose()
  }, [naPagina, open, onClose])

  if (naPagina) return null

  const cabecalho = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
      {iaLigada ? <VigaIcon src={VIGA.icone} size={24} /> : <QuestionAnswerOutlined color="primary" />}
      <Typography variant="h2" sx={{ fontSize: '1.125rem', flexGrow: 1 }}>
        {iaLigada ? t.assistant.titleAi : t.assistant.title}
      </Typography>
      <IconButton aria-label={t.shell.dockClose} onClick={onClose} edge="end">
        <CloseOutlined />
      </IconButton>
    </Box>
  )
  const conteudo = (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
      <AssistantPage modo="painel" />
    </Box>
  )

  if (!open) {
    return (
      <Fab
        aria-label={iaLigada ? t.shell.dockOpenAi : t.shell.dockOpen}
        onClick={onOpen}
        // fundo CLARO: o icone da VIGA do kit e o "claro" (feito para fundo claro); borda para destacar do fundo da tela
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: (th) => th.zIndex.speedDial,
          bgcolor: 'background.paper',
          color: 'primary.main',
          border: 1,
          borderColor: 'divider',
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        {iaLigada ? <VigaIcon src={VIGA.icone} size={32} /> : <QuestionAnswerOutlined />}
      </Fab>
    )
  }

  if (isNarrow) {
    return (
      <Dialog fullScreen open onClose={onClose} aria-label={iaLigada ? t.assistant.titleAi : t.assistant.title}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {cabecalho}
          {conteudo}
        </Box>
      </Dialog>
    )
  }

  return (
    <Box
      component="aside"
      aria-label={iaLigada ? t.assistant.titleAi : t.assistant.title}
      data-testid="viga-dock"
      sx={{
        width: DOCK_WIDTH,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {cabecalho}
      {conteudo}
    </Box>
  )
}
